/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  combineReducers,
  composeReducers,
  composeSetters,
  onChangeReducer,
  setDirty,
  setEntityField,
  setEntityFields,
  setNewDirtyType,
  setUpdatedDirtyType,
  Table,
  toOnChangeReducer,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';

import { find } from 'lodash';

import {
  getJeCostCenterId,
  getJeItemId,
  getJeOrderId,
  JeCostCenter,
  JeGetCostCenterResponse,
  JeOrder,
  mergeLoadedCostCenter,
} from '../../../resources/job-estimate-details.model';
import {
  jeCcTable,
  JeDetailsState,
  JeDetailsStateEvent,
  JeDetailsStateEventType,
  jeItemTable,
  jeOrderTable,
  queryJeCostCenterItems,
  queryJeCostCenterOrders,
} from '../je-details.state';
import { mergeJeItem } from './je-details-cc-item.reducers';
import { mergeJeOrder } from './je-details-order.reducers';
import { selectionConsistencyReducer } from './je-details-selection.reducers';

const setCostCenterField = composeSetters(setEntityField, setUpdatedDirtyType);

// export const updateCostCenterItemCount = (state: JeDetailsState, cc: JeCostCenter) =>
//   setEntityField(cc, 'count', queryJeCostCenterItemCount(state, getJeCostCenterId(cc)));

export const jeDetailsCcReducer = composeReducers(
  loadCostCenterReducer,
  onChangeReducer(
    combineReducers({
      jobCostCenters: costCentersReducer,
      jobOrders: ordersReducer,
    }),
    rootCostCenterOnChangeReducer,
    toOnChangeReducer(selectionConsistencyReducer),
  ),
);

function loadCostCenterReducer(state: JeDetailsState, event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterLoad:
      return mergeCostCenter(state, event.costCenterId, event.costCenter);
    default:
      return state;
  }
}

function costCentersReducer(costCenters: Table<JeCostCenter>,
                            event: JeDetailsStateEvent): Table<JeCostCenter> {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterAdd:
      return jeCcTable.insert(costCenters, setNewDirtyType(event.costCenter));
    case JeDetailsStateEventType.CostCenterSetField:
      return jeCcTable.mergeById(
        costCenters,
        event.costCenterId,
        (row: JeCostCenter) => setCostCenterField(row, event.name, event.value));
    default:
      return costCenters;
  }
}

function ordersReducer(orders: Table<JeOrder>,
                       event: JeDetailsStateEvent): Table<JeOrder> {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterAdd:
      return jeOrderTable.insert(orders, setNewDirtyType(event.order));
    default:
      return orders;
  }
}

function rootCostCenterOnChangeReducer(_: JeDetailsState,
                                       state: JeDetailsState,
                                       event: JeDetailsStateEvent): JeDetailsState {
  switch (event.type) {
    case JeDetailsStateEventType.CostCenterAdd:
    case JeDetailsStateEventType.CostCenterSetField:
      return setDirty(state);
    default:
      return state;
  }
}

export function addOrUpdateCostCenters(state: JeDetailsState,
                                       costCenters: JeCostCenter[],
                                       orders: JeOrder[]): JeDetailsState {
  const ccNewOrUpdatedIds = [state.jobComponentsCcId].concat(costCenters.map(getJeCostCenterId));
  return setEntityFields(state, {
    jobCostCenters: jeCcTable.mergeAllById(
      state.jobCostCenters,
      ccNewOrUpdatedIds,
      // tslint:disable-next-line:no-non-null-assertion
      (row, id) => row ? setUpdatedDirtyType(row) : setNewDirtyType(find(costCenters, { costCenterId: id })!)),
    jobOrders: jeOrderTable.insertAll(state.jobOrders, orders.map(setNewDirtyType)),
  });
}

export function markCostCentersAsDirty(state: JeDetailsState, ids: ID[]): JeDetailsState {
  // sometimes referenced cost centers can absent (due to bugs in legacy application)
  return updateCostCenters(state, ids, (row) => row ? setUpdatedDirtyType(row) : void 0);
}

export function updateCostCenters<T>(state: JeDetailsState,
                                     ids: ID[],
                                     transform: (data: Maybe<JeCostCenter>,
                                                 id: ID) => Maybe<JeCostCenter>): JeDetailsState {
  return setEntityField(
    state,
    'jobCostCenters',
    jeCcTable.mergeAllById(state.jobCostCenters, ids, transform));
}

export function mergeCostCenter(state: JeDetailsState,
                                costCenterId: ID,
                                response: JeGetCostCenterResponse): JeDetailsState {
  return {
    ...state,
    jobCostCenters: jeCcTable.mergeById(
      state.jobCostCenters,
      costCenterId,
      // tslint:disable-next-line:no-non-null-assertion
      (current) => mergeLoadedCostCenter(current!, response.jobCostCenter)),
    jobItems: jeItemTable.replaceAll(
      state.jobItems,
      queryJeCostCenterItems(state, costCenterId).map(getJeItemId),
      response.jobItems,
      mergeJeItem),
    jobOrders: jeOrderTable.replaceAll(
      state.jobOrders,
      queryJeCostCenterOrders(state, costCenterId).map(getJeOrderId),
      response.jobOrders,
      mergeJeOrder),
  };
}
